import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Typography, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, IconButton, Select, MenuItem, Menu, ListItemText, CircularProgress, Divider } from '@mui/material';
import { Application } from '@feathersjs/feathers';
import { District } from '../../app/feathers/districts/District';
import { Shapefile } from '../../app/feathers/districts/Shapefile';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { CheckCircle, PauseCircleOutline, Map, MapOutlined, FilterList, MoreVert, DeleteOutline } from '@mui/icons-material';
import PeopleNotShadedIn from '@mui/icons-material/PeopleAltOutlined';
import PeopleShadedIn from '@mui/icons-material/PeopleAlt';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { asyncPoolAll, useFeathers } from '../../app/util';
import {
	applyColumnTransformsToRow,
	ColumnTransformDefinition,
	getRandomSampleIndexes,
} from './columnTransforms';
import { TransformColumnModal } from './TransformColumnModal';

export interface CreateDistrictDraftRow {
	dataIndex: number;
	primaryValue: string;
	parentValue?: string;
}

export interface CreateDistrictDraft {
	enabled: boolean;
	isSubtype: boolean;
	primaryColumn: string | null;
	parentColumn: string | null;
	rows: CreateDistrictDraftRow[];
	validationError?: string | null;
}

interface ShapefileDataMatcherProps {
	shapefiles: Shapefile[];
	districts: District[];
	stateDistrictId?: string | null;

	// a dictionary of district id to shapefile row index
	pairs: Record<string, number>;
	onChangePairs: (pairs: Record<string, number>) => void;
	stateConfig: StateConfiguration | null;
	districtTypeOptions: string[];
	selectedDistrictType: string;
	onChangeDistrictType: (districtType: string) => void;
	forceCreation?: boolean;

	onChangePopulation?: (population: Record<string, number>) => void;
	onChangeCreateDraft?: (draft: CreateDistrictDraft | null) => void;
	onDeleteShapefileRow?: (dataIndex: number) => Promise<void>;
	style?: React.CSSProperties;
}

const ShapefileDataMatcher = ({
	shapefiles,
	districts,
	stateDistrictId,
	pairs,
	onChangePairs,
	stateConfig,
	districtTypeOptions,
	selectedDistrictType,
	onChangeDistrictType,
	forceCreation = false,
	onChangePopulation,
	onChangeCreateDraft,
	onDeleteShapefileRow,
	style
}: ShapefileDataMatcherProps) => {
	const feathers = useFeathers<Application<ServiceTypes>>();

	// the state keeping tract of active selections
	const [ selectedDistrict, setSelectedDistrict ] = useState<District | null>(null);
	const [ populationColumn, setPopulationColumn ] = useState<string | null>(null);
	const [ statusMessage, setStatusMessage ] = useState<string | null>(null);
	const [ hideDistrictsWithBoundary, setHideDistrictsWithBoundary ] = useState(false);
	const [ columnFilters, setColumnFilters ] = useState<Record<string, string[]>>({});
	const [ openFilterColumn, setOpenFilterColumn ] = useState<string | null>(null);
	const [ filterMenuAnchor, setFilterMenuAnchor ] = useState<HTMLElement | null>(null);
	const [ districtColumnFilters, setDistrictColumnFilters ] = useState<Record<string, string[]>>({});
	const [ openDistrictFilterColumn, setOpenDistrictFilterColumn ] = useState<string | null>(null);
	const [ districtFilterMenuAnchor, setDistrictFilterMenuAnchor ] = useState<HTMLElement | null>(null);
	const [ openColumnActions, setOpenColumnActions ] = useState<string | null>(null);
	const [ columnActionsAnchor, setColumnActionsAnchor ] = useState<HTMLElement | null>(null);
	const [ hiddenShapefileColumns, setHiddenShapefileColumns ] = useState<string[]>([]);
	const [ columnTransforms, setColumnTransforms ] = useState<ColumnTransformDefinition[]>([]);
	const [ transformColumnModalOpen, setTransformColumnModalOpen ] = useState(false);
	const [ transformColumnModalSourceColumn, setTransformColumnModalSourceColumn ] = useState<string | null>(null);
	const [ transformColumnModalRows, setTransformColumnModalRows ] = useState<Shapefile[]>([]);
	const [ transformColumnModalSampleIndexes, setTransformColumnModalSampleIndexes ] = useState<number[]>([]);
	const [ loadingDescendantDistricts, setLoadingDescendantDistricts ] = useState(false);
	const [ descendantDistricts, setDescendantDistricts ] = useState<District[]>([]);
	const [ parentMatchColumn, setParentMatchColumn ] = useState<string | null>(null);
	const [ primaryMatchColumn, setPrimaryMatchColumn ] = useState<string | null>(null);
	const [ matchedDistrictOrder, setMatchedDistrictOrder ] = useState<string[]>(() => Object.keys(pairs));
	const [ matchedShapefileOrder, setMatchedShapefileOrder ] = useState<number[]>(() => Object.values(pairs));
	const [ normalizationIssueDataIndexes, setNormalizationIssueDataIndexes ] = useState<number[]>([]);
	const [ showOnlyNormalizationIssueRows, setShowOnlyNormalizationIssueRows ] = useState(false);
	const [ deletingShapefileRows, setDeletingShapefileRows ] = useState<number[]>([]);
	const tableHeaderSx = {
		'& .MuiTableCell-root': {
			height: '56px',
			boxSizing: 'border-box',
			verticalAlign: 'middle',
		},
	};
	const transformSampleSize = 10;
	const tier2Type = districtTypeOptions[0] || '';
	const isTier3TypeSelected = Boolean(selectedDistrictType && tier2Type && selectedDistrictType !== tier2Type);
	const shouldUseParentColumn = isTier3TypeSelected && !forceCreation;

	const transformedShapefiles = useMemo(() => {
		return shapefiles.map((row) => {
			return applyColumnTransformsToRow(row, columnTransforms);
		});
	}, [shapefiles, columnTransforms]);

	const getDuplicateNormalizationIssueDataIndexes = useCallback((columnName: string, parentColumnName?: string | null) => {
		const validationRows = transformedShapefiles.filter((row) => (
			Object.entries(columnFilters).every(([column, selectedValues]) => {
				if (!selectedValues || selectedValues.length === 0) {
					return true;
				}
				return selectedValues.includes(String(row[column] ?? ''));
			})
		));
		const groupedByNormalizedValue = validationRows.reduce((acc, shapefileRow) => {
			const primaryValue = `${shapefileRow?.[columnName] ?? ''}`.trim();
			if (!primaryValue) {
				return acc;
			}
			const normalizedPrimaryValue = valueForMatch(primaryValue);
			if (!normalizedPrimaryValue) {
				return acc;
			}
			const key = shouldUseParentColumn
				? (() => {
					const parentValue = `${shapefileRow?.[parentColumnName || ''] ?? ''}`.trim();
					if (!parentValue) {
						return null;
					}
					const normalizedParentValue = valueForMatch(parentValue);
					if (!normalizedParentValue) {
						return null;
					}
					return `${normalizedParentValue}__${normalizedPrimaryValue}`;
				})()
				: normalizedPrimaryValue;

			if (!key) {
				return acc;
			}

			if (!acc[key]) {
				acc[key] = [];
			}
			acc[key].push(shapefileRow.dataIndex);
			return acc;
		}, {} as Record<string, number[]>);

		const duplicateGroups = Object.entries(groupedByNormalizedValue)
			.filter(([, indexes]) => indexes.length > 1);

		return duplicateGroups
			.map(([, indexes]) => indexes)
			.flat()
			.sort((a, b) => a - b);
	}, [transformedShapefiles, shouldUseParentColumn, columnFilters]);

	const setPrimaryMatchColumnIfValid = useCallback((columnName: string) => {
		const duplicateDataIndexes = getDuplicateNormalizationIssueDataIndexes(columnName, parentMatchColumn);
		if (duplicateDataIndexes.length > 0) {
			setNormalizationIssueDataIndexes(duplicateDataIndexes);
			setShowOnlyNormalizationIssueRows(true);
			setStatusMessage(`Cannot use "${columnName}" as primary column: ${duplicateDataIndexes.length} row(s) are not unique after normalization.`);
			return;
		}
		setPrimaryMatchColumn(columnName);
		setNormalizationIssueDataIndexes([]);
		setShowOnlyNormalizationIssueRows(false);
		setStatusMessage(null);
	}, [getDuplicateNormalizationIssueDataIndexes, parentMatchColumn]);

	const allShapefileHeaders = useMemo(() => {
		if(transformedShapefiles.length === 0) return [];
		return Object.keys(transformedShapefiles?.[0] || {}).filter(k => k !== 'dataIndex' && k !== 'geometry');
	}, [transformedShapefiles]);

	const shapefileHeaders = useMemo(() => {
		return allShapefileHeaders.filter((header) => !hiddenShapefileColumns.includes(header));
	}, [allShapefileHeaders, hiddenShapefileColumns]);

	const populationColumnPotentials = useMemo(() => {
		if(transformedShapefiles.length === 0) return [];
		// only return columns with pop who's values are whole numbers
		return allShapefileHeaders.filter(h => h && h.toLowerCase().includes('pop') && transformedShapefiles.every(s => {
			const val = s[h];
			return !isNaN(parseInt(val)) && val === Math.floor(val);
		}))
	}, [ allShapefileHeaders, transformedShapefiles ]);

	// prep the districts and shapefile rows by ordering them
	const districtRows = useMemo<District[]>(() => {
		// sort by paired rows first 
		const ordered = matchedDistrictOrder.map(k => {
			return districts.find(d => d._id === k)
		}).filter((d): d is District => d !== undefined);
		const rest = districts.filter(d => !Object.keys(pairs).includes(d._id))
		const allDistricts = [...ordered, ...rest];
		
		// Filter out districts with boundaries if the filter is active
		return hideDistrictsWithBoundary 
			? allDistricts.filter(d => !d.hasGeodistrict)
			: allDistricts;
	}, [ districts, pairs, hideDistrictsWithBoundary, matchedDistrictOrder ])


	const shapefileRows = useMemo<Shapefile[]>(() => {
		// order according to the row pairs / children district orders
		let ordered = matchedShapefileOrder.map(k => {
			return transformedShapefiles.find(d => d.dataIndex === k)
		}).filter((d): d is Shapefile => d !== undefined);
		// add the rest of the rows
		const rest = transformedShapefiles.filter(d => !Object.values(pairs).includes(d.dataIndex))
		const allRows = [...ordered, ...rest];
		const normalizationIssueIndexes = new Set(normalizationIssueDataIndexes);

		return allRows.filter((row) => {
			if (showOnlyNormalizationIssueRows && normalizationIssueIndexes.size > 0 && !normalizationIssueIndexes.has(row.dataIndex)) {
				return false;
			}
			return Object.entries(columnFilters).every(([column, selectedValues]) => {
				if (!selectedValues || selectedValues.length === 0) {
					return true;
				}
				const value = String(row[column] ?? '');
				return selectedValues.includes(value);
			});
		});
  }, [transformedShapefiles, districtRows, pairs, columnFilters, matchedShapefileOrder, showOnlyNormalizationIssueRows, normalizationIssueDataIndexes])

	const uniqueValuesByHeader = useMemo(() => {
		return allShapefileHeaders.reduce((acc, header) => {
			acc[header] = [...new Set(transformedShapefiles.map((row) => String(row[header] ?? '')))].sort();
			return acc;
		}, {} as Record<string, string[]>);
	}, [transformedShapefiles, allShapefileHeaders]);

	const descendantDistrictRows = useMemo<District[]>(() => {
		const sortByParentThenName = (a: District, b: District) => {
			const parentA = (a.parent || '').toLowerCase();
			const parentB = (b.parent || '').toLowerCase();
			if (parentA !== parentB) {
				return parentA.localeCompare(parentB);
			}

			const parsedNumberA = Number(a.number);
			const parsedNumberB = Number(b.number);
			const hasNumberA = Number.isFinite(parsedNumberA);
			const hasNumberB = Number.isFinite(parsedNumberB);

			if (hasNumberA && hasNumberB && parsedNumberA !== parsedNumberB) {
				return parsedNumberA - parsedNumberB;
			}
			if (hasNumberA !== hasNumberB) {
				return hasNumberA ? -1 : 1;
			}

			const nameA = (a.name || '').toLowerCase();
			const nameB = (b.name || '').toLowerCase();
			if (nameA !== nameB) {
				return nameA.localeCompare(nameB);
			}

			const letterA = (a.letter || '').toLowerCase();
			const letterB = (b.letter || '').toLowerCase();
			if (letterA !== letterB) {
				return letterA.localeCompare(letterB);
			}

			const longNameA = (a.longName || '').toLowerCase();
			const longNameB = (b.longName || '').toLowerCase();
			return longNameA.localeCompare(longNameB);
		};

		const ordered = matchedDistrictOrder.map((districtId) => {
			return descendantDistricts.find((district) => district._id === districtId);
		}).filter((district): district is District => district !== undefined);
		const rest = descendantDistricts
			.filter((district) => !Object.keys(pairs).includes(district._id))
			.sort(sortByParentThenName);
		return [...ordered, ...rest];
	}, [descendantDistricts, pairs, matchedDistrictOrder]);

	const activeDistrictRows = shouldUseParentColumn
		? descendantDistrictRows
		: districtRows;

	const parentDistrictLookup = useMemo(() => {
		const byId: Record<string, District> = {};
		const byNormalizedValue: Record<string, District> = {};

		districts.forEach((district) => {
			byId[district._id] = district;
			[
				district._id,
				district.matchName,
				district.longName,
				district.shortName,
				district.name,
				district.fipsCode,
			].forEach((value) => {
				const normalized = valueForMatch(`${value ?? ''}`);
				if (normalized && !byNormalizedValue[normalized]) {
					byNormalizedValue[normalized] = district;
				}
			});
		});

		return { byId, byNormalizedValue };
	}, [districts]);

	const getDistrictParentDisplayName = useCallback((district: District) => {
		const parentId = `${district.parentId ?? ''}`.trim();
		if (parentId && parentDistrictLookup.byId[parentId]) {
			return parentDistrictLookup.byId[parentId].longName || parentDistrictLookup.byId[parentId].name;
		}
		const normalizedParentValue = valueForMatch(`${district.parent ?? ''}`);
		if (normalizedParentValue && parentDistrictLookup.byNormalizedValue[normalizedParentValue]) {
			const parentDistrict = parentDistrictLookup.byNormalizedValue[normalizedParentValue];
			return parentDistrict.longName || parentDistrict.name;
		}
		return district.parent;
	}, [parentDistrictLookup]);

	const getParentDisplayNameFromRawValue = useCallback((parentRawValue?: string, parentId?: string | null) => {
		const normalizedParentId = `${parentId ?? ''}`.trim();
		if (normalizedParentId && parentDistrictLookup.byId[normalizedParentId]) {
			const parentDistrict = parentDistrictLookup.byId[normalizedParentId];
			return parentDistrict.longName || parentDistrict.name;
		}
		const normalizedParentValue = valueForMatch(`${parentRawValue ?? ''}`);
		if (normalizedParentValue && parentDistrictLookup.byNormalizedValue[normalizedParentValue]) {
			const parentDistrict = parentDistrictLookup.byNormalizedValue[normalizedParentValue];
			return parentDistrict.longName || parentDistrict.name;
		}
		return parentRawValue;
	}, [parentDistrictLookup]);

	const filteredActiveDistrictRows = useMemo<District[]>(() => {
		return activeDistrictRows.filter((row) => {
			return Object.entries(districtColumnFilters).every(([column, selectedValues]) => {
				if (!selectedValues || selectedValues.length === 0) {
					return true;
				}
				const value = column === 'parent'
					? String(getDistrictParentDisplayName(row))
					: String(row[column as keyof District] ?? '');
				return selectedValues.includes(value);
			});
		});
	}, [activeDistrictRows, districtColumnFilters, getDistrictParentDisplayName]);

	const noDistrictsForSelectedType = !loadingDescendantDistricts && activeDistrictRows.length === 0;
	const canBuildSubtypePreview = !shouldUseParentColumn || Boolean(parentMatchColumn);
	const previewRows = useMemo(() => {
		if (!noDistrictsForSelectedType || !primaryMatchColumn || !canBuildSubtypePreview) {
			return [] as ({
				draftId: string;
				longName: string;
				parent?: string;
				dataIndex: number;
				primaryValue: string;
				parentValue?: string;
			})[];
		}
		return shapefileRows.reduce((acc, shapefileRow) => {
			const primaryValueRaw = shapefileRow?.[primaryMatchColumn];
			const parentValueRaw = parentMatchColumn ? shapefileRow?.[parentMatchColumn] : undefined;
			const primaryValue = `${primaryValueRaw ?? ''}`.trim();
			const parentValue = `${parentValueRaw ?? ''}`.trim();
			if (!primaryValue) {
				return acc;
			}
			if (shouldUseParentColumn && !parentValue) {
				return acc;
			}
			acc.push({
				draftId: `draft-${shapefileRow.dataIndex}`,
				longName: primaryValue,
				parent: getParentDisplayNameFromRawValue(parentValue || undefined),
				dataIndex: shapefileRow.dataIndex,
				primaryValue,
				parentValue: parentValue || undefined,
			});
			return acc;
		}, [] as {
			draftId: string;
			longName: string;
			parent?: string;
			dataIndex: number;
			primaryValue: string;
			parentValue?: string;
		}[]);
	}, [noDistrictsForSelectedType, primaryMatchColumn, canBuildSubtypePreview, shapefileRows, parentMatchColumn, shouldUseParentColumn, getParentDisplayNameFromRawValue]);

	const displayDistrictRows = noDistrictsForSelectedType && primaryMatchColumn
		? previewRows
		: filteredActiveDistrictRows;

	const lineCount = noDistrictsForSelectedType && primaryMatchColumn
		? previewRows.length
		: Object.keys(pairs).length;

	const createDraftValidationError = useMemo(() => {
		if (!noDistrictsForSelectedType || !primaryMatchColumn) {
			return null;
		}
		const missingPrimaryRows = shapefileRows.filter((row) => !`${row?.[primaryMatchColumn] ?? ''}`.trim());
		if (missingPrimaryRows.length > 0) {
			return `Primary column has ${missingPrimaryRows.length} blank value(s).`;
		}
		if (shouldUseParentColumn) {
			if (!parentMatchColumn) {
				return 'Select a parent column before selecting the primary column.';
			}
			const missingParentRows = shapefileRows.filter((row) => !`${row?.[parentMatchColumn] ?? ''}`.trim());
			if (missingParentRows.length > 0) {
				return `Parent column has ${missingParentRows.length} blank value(s).`;
			}
		}
		const duplicateMap = previewRows.reduce((acc, row) => {
			const baseKey = valueForMatch(row.primaryValue);
			const key = shouldUseParentColumn
				? `${valueForMatch(row.parentValue || '')}__${baseKey}`
				: baseKey;
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {} as Record<string, number>);
		const duplicateKeys = Object.entries(duplicateMap).filter(([, count]) => count > 1);
		if (duplicateKeys.length > 0) {
			return `Primary column has ${duplicateKeys.length} duplicate normalized value group(s).`;
		}
		return null;
	}, [noDistrictsForSelectedType, primaryMatchColumn, shapefileRows, shouldUseParentColumn, parentMatchColumn, previewRows]);

	const uniqueDistrictValuesByColumn = useMemo(() => {
		const columns = shouldUseParentColumn ? ['parent', 'longName'] : ['longName'];
		return columns.reduce((acc, column) => {
			acc[column] = [...new Set(activeDistrictRows.map((row) => (
				column === 'parent'
					? String(getDistrictParentDisplayName(row))
					: String(row[column as keyof District] ?? '')
			)))].sort();
			return acc;
		}, {} as Record<string, string[]>);
	}, [activeDistrictRows, shouldUseParentColumn, getDistrictParentDisplayName]);

	const pairChildDistrictToShapefileRow = (district: District, shapefileRow: Shapefile) => {
    console.log(`Pairing ${district.longName} with ${shapefileRow?.dataIndex}`)
    // remove all other pairings for this child district and shapefile row
		// because sometimes we might want to reassign a district to a different shapefile row
    const trimmedPairs = Object.entries(pairs).reduce((acc, [districtId, dataIndex]) => {
      if(districtId === district._id || dataIndex === shapefileRow.dataIndex) { 
				return acc;
      } else {
				acc[districtId] = dataIndex;
				return acc;
			}
    }, {} as Record<string, number>)

    const newPairs = {
      ...trimmedPairs,
      [district._id]: shapefileRow.dataIndex
    }
		setMatchedDistrictOrder((currentOrder) => [
			...currentOrder.filter((districtId) => districtId !== district._id),
			district._id,
		]);
		setMatchedShapefileOrder((currentOrder) => [
			...currentOrder.filter((dataIndex) => dataIndex !== shapefileRow.dataIndex),
			shapefileRow.dataIndex,
		]);
    onChangePairs(newPairs)
    setSelectedDistrict(null)
  }

	const numberPaired: number = Object.keys(pairs).length;

	const deleteShapefileRow = async (dataIndex: number) => {
		if (!onDeleteShapefileRow || deletingShapefileRows.includes(dataIndex)) {
			return;
		}
		setStatusMessage(null);
		setDeletingShapefileRows((current) => [...current, dataIndex]);
		try {
			await onDeleteShapefileRow(dataIndex);
		} catch (error) {
			console.error('[ShapefileDataMatcher] failed deleting shapefile row', { dataIndex, error });
			setStatusMessage(error instanceof Error ? error.message : 'Failed to delete shapefile row.');
		} finally {
			setDeletingShapefileRows((current) => current.filter((rowDataIndex) => rowDataIndex !== dataIndex));
		}
	};

	// This function takes in a shapefile column and attempts to find the corresponding field in the districts
	const attemptMatchByColumn = (columnName: string) => {
		setStatusMessage(null);

		const districtToShapefileIndex: Record<string, number> = {};
		const candidateShapefiles = shapefileRows;

		// Get filtered districts based on checkbox
		const filteredDistricts = hideDistrictsWithBoundary 
			? filteredActiveDistrictRows.filter((d) => !d.hasGeodistrict)
			: filteredActiveDistrictRows;

		// identifier type for districts
		const identifierType = filteredDistricts.some(d => d.name) ? 'name' : (filteredDistricts.some(d => d.letter) ? 'letter' : 'number');

		// if the shapefiles are nonunique, do not match on those.
		const shapefilesUniqueOnColumn = candidateShapefiles.filter((shapefile, idx, arr) => {
			const columnValue = valueForMatch(`${shapefile[columnName]}`);
			const otherMatchingRows = arr.filter((s, i) => i !== idx && valueForMatch(`${s[columnName]}`) === columnValue);
			return otherMatchingRows.length === 0;
		})
			
		const shapefileValuesToIndex = shapefilesUniqueOnColumn.reduce((acc: Record<string, number> | null, shapefile: Shapefile) => {
			if(acc === null) return null;
			// first of all, make sure that this column is both unique and fully populated
			if(!shapefile[columnName]) {
				setStatusMessage('The column you selected has missing values.');
				return null;
			}
			const columnValue = shapefile[columnName];
			// massage the column to make it lowercase, and remove common prefixes like 'district of', 'county' etc
			const columnValueForMatch = valueForMatch(`${columnValue}`);
			if(acc[columnValueForMatch]) {
				setStatusMessage('The column you selected is not unique: ' + columnValueForMatch + '. We will attempt to match how we can.');
				return null;
			}
			acc[columnValueForMatch] = shapefile.dataIndex;
			return acc;
		}, {} as Record<string, number>);
		if(shapefileValuesToIndex === null) {
			onChangePairs({});
			return;
		}

		// now, try to match the districts with the shapefile values
		filteredDistricts.forEach((district) => {
			const districtValue = `${district[identifierType]}`.toLowerCase();
			const shapefileIndex = shapefileValuesToIndex[districtValue];
			if(typeof(shapefileIndex) !== 'undefined') {
				districtToShapefileIndex[district._id] = shapefileIndex;
			} else {
				// try to match based on the longName and short name
				const districtValueLong = valueForMatch(`${district.longName}`);
				// make sure that this is unique across the array
				const matchingDistricts = filteredDistricts.filter(d => valueForMatch(`${d.longName}`) === districtValueLong);
				let shapefileIndex: number | undefined;
				if(matchingDistricts.length === 1) {
					shapefileIndex = shapefileValuesToIndex[districtValueLong];
				}

				if(typeof(shapefileIndex) === 'undefined') {
					const districtValueShort = valueForMatch(`${district.shortName}`);
					const matchingDistricts = filteredDistricts.filter(d => valueForMatch(`${d.shortName}`) === districtValueShort);
					if(matchingDistricts.length === 1) {
						shapefileIndex = shapefileValuesToIndex[districtValueShort];
					}
				}

				if(typeof(shapefileIndex) !== 'undefined') {
					districtToShapefileIndex[district._id] = shapefileIndex;
				}
			}
		});

		onChangePairs(districtToShapefileIndex);
	}

	const attemptMatchByParentAndColumn = (parentColumnName: string, columnName: string) => {
		setStatusMessage(null);

		const districtToShapefileIndex: Record<string, number> = {};
		const candidateShapefiles = shapefileRows;
		const filteredDistricts = hideDistrictsWithBoundary
			? filteredActiveDistrictRows.filter((d) => !d.hasGeodistrict)
			: filteredActiveDistrictRows;

		const identifierType = filteredDistricts.some(d => d.name)
			? 'name'
			: (filteredDistricts.some(d => d.letter) ? 'letter' : 'number');

		const shapefileValuesToIndex = candidateShapefiles.reduce((acc: Record<string, number> | null, shapefile: Shapefile) => {
			if (acc === null) return null;

			const parentValue = shapefile[parentColumnName];
			const columnValue = shapefile[columnName];

			if (!parentValue || !columnValue) {
				setStatusMessage('The selected parent/column has missing values.');
				return null;
			}

			const parentValueForMatch = valueForMatch(`${parentValue}`);
			const columnValueForMatch = valueForMatch(`${columnValue}`);
			const key = `${parentValueForMatch}__${columnValueForMatch}`;

			if (acc[key]) {
				setStatusMessage(`The parent + column combination is not unique: ${key}.`);
				return null;
			}
			acc[key] = shapefile.dataIndex;
			return acc;
		}, {} as Record<string, number>);

		if (shapefileValuesToIndex === null) {
			onChangePairs({});
			return;
		}

		filteredDistricts.forEach((district) => {
			const districtParentValue = valueForMatch(`${district.parent || ''}`);
			const districtColumnRaw = (
				identifierType === 'name'
					? district.name
					: identifierType === 'letter'
						? district.letter
						: district.number
			) || district.name || district.longName || district.shortName;
			const districtColumnValue = valueForMatch(`${districtColumnRaw || ''}`);
			const key = `${districtParentValue}__${districtColumnValue}`;
			const shapefileIndex = shapefileValuesToIndex[key];
			if (typeof(shapefileIndex) !== 'undefined') {
				districtToShapefileIndex[district._id] = shapefileIndex;
			}
		});

		onChangePairs(districtToShapefileIndex);
	};

	const attemptMatchByFipsCode = (columnName: string) => {
		setStatusMessage(null);

		const districtToShapefileIndex: Record<string, number> = {};
		const candidateShapefiles = shapefileRows;
		const filteredDistricts = hideDistrictsWithBoundary
			? filteredActiveDistrictRows.filter((d) => !d.hasGeodistrict)
			: filteredActiveDistrictRows;
		const districtsWithFips = filteredDistricts.filter((district) => `${district.fipsCode || ''}`.trim());

		if (districtsWithFips.length === 0) {
			setStatusMessage('No districts have FIPS code values.');
			onChangePairs({});
			return;
		}

		const shapefileValuesToIndex = candidateShapefiles.reduce((acc: Record<string, number> | null, shapefile: Shapefile) => {
			if (acc === null) return null;
			if (!shapefile[columnName]) {
				setStatusMessage('The column you selected has missing values.');
				return null;
			}
			const columnValueForMatch = valueForMatch(`${shapefile[columnName]}`);
			if (acc[columnValueForMatch]) {
				setStatusMessage(`The column you selected is not unique: ${columnValueForMatch}.`);
				return null;
			}
			acc[columnValueForMatch] = shapefile.dataIndex;
			return acc;
		}, {} as Record<string, number>);

		if (shapefileValuesToIndex === null) {
			onChangePairs({});
			return;
		}

		districtsWithFips.forEach((district) => {
			const districtFips = valueForMatch(`${district.fipsCode || ''}`);
			const shapefileIndex = shapefileValuesToIndex[districtFips];
			if (typeof(shapefileIndex) !== 'undefined') {
				districtToShapefileIndex[district._id] = shapefileIndex;
			}
		});

		onChangePairs(districtToShapefileIndex);
	};

	const attemptMatchByParentAndFipsCode = (parentColumnName: string, columnName: string) => {
		setStatusMessage(null);

		const districtToShapefileIndex: Record<string, number> = {};
		const candidateShapefiles = shapefileRows;
		const filteredDistricts = hideDistrictsWithBoundary
			? filteredActiveDistrictRows.filter((d) => !d.hasGeodistrict)
			: filteredActiveDistrictRows;
		const districtsWithFips = filteredDistricts.filter((district) => `${district.fipsCode || ''}`.trim());

		if (districtsWithFips.length === 0) {
			setStatusMessage('No districts have FIPS code values.');
			onChangePairs({});
			return;
		}

		const shapefileValuesToIndex = candidateShapefiles.reduce((acc: Record<string, number> | null, shapefile: Shapefile) => {
			if (acc === null) return null;

			const parentValue = shapefile[parentColumnName];
			const columnValue = shapefile[columnName];

			if (!parentValue || !columnValue) {
				setStatusMessage('The selected parent/column has missing values.');
				return null;
			}

			const parentValueForMatch = valueForMatch(`${parentValue}`);
			const columnValueForMatch = valueForMatch(`${columnValue}`);
			const key = `${parentValueForMatch}__${columnValueForMatch}`;
			if (acc[key]) {
				setStatusMessage(`The parent + column combination is not unique: ${key}.`);
				return null;
			}
			acc[key] = shapefile.dataIndex;
			return acc;
		}, {} as Record<string, number>);

		if (shapefileValuesToIndex === null) {
			onChangePairs({});
			return;
		}

		districtsWithFips.forEach((district) => {
			const districtParentValue = valueForMatch(`${district.parent || ''}`);
			const districtFipsValue = valueForMatch(`${district.fipsCode || ''}`);
			const key = `${districtParentValue}__${districtFipsValue}`;
			const shapefileIndex = shapefileValuesToIndex[key];
			if (typeof(shapefileIndex) !== 'undefined') {
				districtToShapefileIndex[district._id] = shapefileIndex;
			}
		});

		onChangePairs(districtToShapefileIndex);
	};

	const openColumnFilter = (header: string, anchorEl: HTMLElement) => {
		setOpenFilterColumn(header);
		setFilterMenuAnchor(anchorEl);
	};

	const closeColumnFilter = () => {
		setOpenFilterColumn(null);
		setFilterMenuAnchor(null);
	};

	const toggleColumnFilterValue = (header: string, value: string) => {
		setColumnFilters((currentFilters) => {
			const previousValues = currentFilters[header] || [];
			const nextValues = previousValues.includes(value)
				? previousValues.filter((v) => v !== value)
				: [...previousValues, value];
			return {
				...currentFilters,
				[header]: nextValues,
			};
		});
	};

	const clearColumnFilter = (header: string) => {
		setColumnFilters((currentFilters) => ({
			...currentFilters,
			[header]: [],
		}));
	};

	const openDistrictColumnFilter = (header: string, anchorEl: HTMLElement) => {
		setOpenDistrictFilterColumn(header);
		setDistrictFilterMenuAnchor(anchorEl);
	};

	const closeDistrictColumnFilter = () => {
		setOpenDistrictFilterColumn(null);
		setDistrictFilterMenuAnchor(null);
	};

	const toggleDistrictColumnFilterValue = (header: string, value: string) => {
		setDistrictColumnFilters((currentFilters) => {
			const previousValues = currentFilters[header] || [];
			const nextValues = previousValues.includes(value)
				? previousValues.filter((v) => v !== value)
				: [...previousValues, value];
			return {
				...currentFilters,
				[header]: nextValues,
			};
		});
	};

	const clearDistrictColumnFilter = (header: string) => {
		setDistrictColumnFilters((currentFilters) => ({
			...currentFilters,
			[header]: [],
		}));
	};

	const openColumnActionsMenu = (header: string, anchorEl: HTMLElement) => {
		setOpenColumnActions(header);
		setColumnActionsAnchor(anchorEl);
	};

	const closeColumnActionsMenu = () => {
		setOpenColumnActions(null);
		setColumnActionsAnchor(null);
	};

	const hideShapefileColumn = (header: string) => {
		setHiddenShapefileColumns((current) => {
			if(current.includes(header)) return current;
			return [...current, header];
		});
		if(populationColumn === header) {
			setPopulationColumn(null);
		}
	};

	const openTransformColumnModal = (header: string) => {
		setTransformColumnModalSourceColumn(header);
		setTransformColumnModalRows(shapefileRows);
		setTransformColumnModalSampleIndexes(
			getRandomSampleIndexes(shapefileRows.length, transformSampleSize, Date.now())
		);
		setTransformColumnModalOpen(true);
	};

	const closeTransformColumnModal = () => {
		setTransformColumnModalOpen(false);
		setTransformColumnModalSourceColumn(null);
		setTransformColumnModalRows([]);
		setTransformColumnModalSampleIndexes([]);
	};

	const applyTransformColumnDefinition = (definition: ColumnTransformDefinition) => {
		setColumnTransforms((current) => [...current, definition]);
		setHiddenShapefileColumns((current) => current.filter((column) => column !== definition.target));
		closeTransformColumnModal();
	};

	useEffect(() => {
		const pairedDistrictIds = Object.keys(pairs);
		setMatchedDistrictOrder((currentOrder) => {
			const nextOrder = currentOrder.filter((districtId) => pairedDistrictIds.includes(districtId));
			pairedDistrictIds.forEach((districtId) => {
				if (!nextOrder.includes(districtId)) {
					nextOrder.push(districtId);
				}
			});
			return nextOrder;
		});

		const pairedDataIndexes = Object.values(pairs);
		setMatchedShapefileOrder((currentOrder) => {
			const nextOrder = currentOrder.filter((dataIndex) => pairedDataIndexes.includes(dataIndex));
			pairedDataIndexes.forEach((dataIndex) => {
				if (!nextOrder.includes(dataIndex)) {
					nextOrder.push(dataIndex);
				}
			});
			return nextOrder;
		});
	}, [pairs]);

	useEffect(() => {
		if(populationColumn && onChangePopulation) {
			// try to go through and find the population values, returning it by district id to population value
			const populationValues: Record<string, number> = {};
			Object.entries(pairs).forEach(([districtId, shapefileIndex]) => {
				const shapefile = transformedShapefiles.find(s => s.dataIndex === shapefileIndex);
				if(shapefile && shapefile[populationColumn] && !isNaN(parseInt(shapefile[populationColumn]))) {
					populationValues[districtId] = parseInt(shapefile[populationColumn]);
				}
			})

			onChangePopulation(populationValues);
		}
	}, [ populationColumn, pairs, transformedShapefiles, onChangePopulation ])

	useEffect(() => {
		if (selectedDistrict && !filteredActiveDistrictRows.some((district) => district._id === selectedDistrict._id)) {
			setSelectedDistrict(null);
		}
	}, [filteredActiveDistrictRows, selectedDistrict]);

	useEffect(() => {
		if (!shouldUseParentColumn) {
			setParentMatchColumn(null);
		}
	}, [shouldUseParentColumn]);

	useEffect(() => {
		if (!noDistrictsForSelectedType) {
			setPrimaryMatchColumn(null);
		}
	}, [noDistrictsForSelectedType]);

	useEffect(() => {
		if (!noDistrictsForSelectedType) {
			setNormalizationIssueDataIndexes([]);
			setShowOnlyNormalizationIssueRows(false);
			return;
		}
		if (!primaryMatchColumn) {
			return;
		}
		if (shouldUseParentColumn && !parentMatchColumn) {
			return;
		}
		const duplicateDataIndexes = getDuplicateNormalizationIssueDataIndexes(primaryMatchColumn, parentMatchColumn);
		setNormalizationIssueDataIndexes(duplicateDataIndexes);
		if (duplicateDataIndexes.length === 0) {
			setShowOnlyNormalizationIssueRows(false);
		}
	}, [
		noDistrictsForSelectedType,
		primaryMatchColumn,
		parentMatchColumn,
		shouldUseParentColumn,
		getDuplicateNormalizationIssueDataIndexes,
	]);

	useEffect(() => {
		if (!onChangeCreateDraft) {
			return;
		}
		if (!noDistrictsForSelectedType) {
			onChangeCreateDraft(null);
			return;
		}
		const isSubtypeDraft = shouldUseParentColumn;
		const hasPrimary = Boolean(primaryMatchColumn);
		const hasParentRequirementMet = !isSubtypeDraft || Boolean(parentMatchColumn);
		onChangeCreateDraft({
			enabled: hasPrimary && hasParentRequirementMet && previewRows.length > 0 && !createDraftValidationError,
			isSubtype: isSubtypeDraft,
			primaryColumn: primaryMatchColumn,
			parentColumn: parentMatchColumn,
			rows: previewRows.map((row) => ({
				dataIndex: row.dataIndex,
				primaryValue: row.primaryValue,
				parentValue: row.parentValue,
			})),
			validationError: createDraftValidationError,
		});
	}, [
		onChangeCreateDraft,
		noDistrictsForSelectedType,
		shouldUseParentColumn,
		primaryMatchColumn,
		parentMatchColumn,
		previewRows,
		createDraftValidationError,
	]);

	useEffect(() => {
		if (!feathers || forceCreation || !selectedDistrictType || !tier2Type || selectedDistrictType === tier2Type) {
			setLoadingDescendantDistricts(false);
			setDescendantDistricts([]);
			return;
		}
		if (!stateDistrictId) {
			setLoadingDescendantDistricts(false);
			setDescendantDistricts([]);
			return;
		}

		let cancelled = false;
		const loadMatchingTypeDistricts = async () => {
			try {
				setLoadingDescendantDistricts(true);
				const limit = 500;
				const baseQuery = {
					type: selectedDistrictType,
					state: stateDistrictId,
					$sort: {
						createdAt: 1
					}
				};
				const countResult = await feathers.getService('districts').find({
					query: {
						...baseQuery,
						$limit: 0,
					},
				});
				const total = Array.isArray(countResult) ? countResult.length : (countResult.total || 0);
				const pages = Math.ceil(total / limit);
				const pageIndexes = Array.from({ length: pages }, (_, i) => i);

				const pageResults = await asyncPoolAll(10, pageIndexes, async (i: number) => {
					const result = await feathers.getService('districts').find({
						query: {
							...baseQuery,
							$limit: limit,
							$skip: i * limit,
						},
					});
					return Array.isArray(result) ? result : (result.data || []);
				});
				const matchedDistricts = pageResults.flat();

				console.log('[ShapefileDataMatcher] loaded descendant districts for selected type', {
					selectedDistrictType,
					stateDistrictId,
					count: matchedDistricts?.length || 0,
					districts: matchedDistricts,
				});
				if (!cancelled) {
					setDescendantDistricts(matchedDistricts);
				}
			} catch (error) {
				console.error('[ShapefileDataMatcher] failed loading descendant districts', {
					selectedDistrictType,
					stateDistrictId,
					error,
				});
				if (!cancelled) {
					setDescendantDistricts([]);
				}
			} finally {
				if (!cancelled) {
					setLoadingDescendantDistricts(false);
				}
			}
		};

		loadMatchingTypeDistricts();

		return () => {
			cancelled = true;
		};
	}, [feathers, forceCreation, selectedDistrictType, tier2Type, stateDistrictId]);

	return (
		<div style={{ ...style }}>
			<div style={{ maxHeight: '500px', overflowY: 'scroll' }}>
				<div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Typography variant='body2' style={{ minWidth: '90px' }}>
							District type
						</Typography>
						<Select
							size="small"
							value={selectedDistrictType}
							onChange={(e) => onChangeDistrictType(e.target.value as string)}
							style={{ minWidth: '240px' }}
							disabled={forceCreation || districtTypeOptions.length === 0 || loadingDescendantDistricts}
						>
							{districtTypeOptions.map((districtType) => (
								<MenuItem key={districtType} value={districtType}>
									{humanizeDistrictType(districtType, stateConfig?.typeConfigurations || null)}
								</MenuItem>
							))}
						</Select>
						{
							loadingDescendantDistricts &&
							<CircularProgress size={18} />
						}
					</div>
				</div>
				{
					statusMessage && 
					<Typography variant='body1' style={{ color: 'red', marginBottom: '8px' }}>{statusMessage}</Typography>
				}
				{
					noDistrictsForSelectedType &&
					<div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
						<Typography variant='body2'>
							No districts yet, select a primary column to get started.
						</Typography>
						{
							shouldUseParentColumn && !parentMatchColumn &&
							<Typography variant='caption' color='textSecondary'>
								Select a parent column first, then choose the primary column.
							</Typography>
						}
						{
							createDraftValidationError &&
							<Typography variant='caption' color='error'>
								{createDraftValidationError}
							</Typography>
						}
					</div>
				}
				<TwoSideSplit>
					<SplitInner>
						{
							normalizationIssueDataIndexes.length > 0 &&
							<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Tooltip title={showOnlyNormalizationIssueRows ? 'Show all rows' : 'Show only rows with normalization collisions'}>
									<IconButton
										size='small'
										onClick={() => setShowOnlyNormalizationIssueRows((current) => !current)}
										style={showOnlyNormalizationIssueRows ? { backgroundColor: '#DCE8FF' } : {}}
									>
										<FilterList style={{ fontSize: '16px' }} />
									</IconButton>
								</Tooltip>
								<Typography variant='caption' style={{ alignSelf: 'center', marginLeft: '8px' }}>
									Issue rows: {normalizationIssueDataIndexes.length}
								</Typography>
							</div>
						}
						<TableContainer component={Paper} style={{ width: '100%', overflowX: 'scroll' }}>
							<Table>
								<TableHead sx={tableHeaderSx}>
									<TableRow>
										{
											shouldUseParentColumn &&
											<TableCell>
												<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
													<Typography variant='body2' style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
														Parent name
													</Typography>
													<Tooltip title='Filter by this column'>
														<IconButton
															size='small'
															onClick={(e) => openDistrictColumnFilter('parent', e.currentTarget)}
															style={(districtColumnFilters.parent || []).length > 0
																? { backgroundColor: '#DCE8FF' }
																: {}
															}
														>
															<FilterList style={{ fontSize: '16px' }} />
														</IconButton>
													</Tooltip>
												</div>
											</TableCell>
										}
										<TableCell>
											<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
												<Typography variant='body2' style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
													District name
												</Typography>
												<Tooltip
													title={
														hideDistrictsWithBoundary
															? 'Show districts with geoboundary already uploaded'
															: 'Hide districts with geoboundary already uploaded'
													}
												>
													<IconButton
														size='small'
														onClick={() => setHideDistrictsWithBoundary(!hideDistrictsWithBoundary)}
														style={hideDistrictsWithBoundary ? { backgroundColor: '#DCE8FF' } : {}}
													>
														{
															hideDistrictsWithBoundary
																? <Map style={{ fontSize: '16px' }} />
																: <MapOutlined style={{ fontSize: '16px' }} />
														}
													</IconButton>
												</Tooltip>
												<Tooltip title='Filter by this column'>
													<IconButton
														size='small'
														onClick={(e) => openDistrictColumnFilter('longName', e.currentTarget)}
														style={(districtColumnFilters.longName || []).length > 0
															? { backgroundColor: '#DCE8FF' }
															: {}
														}
													>
														<FilterList style={{ fontSize: '16px' }} />
													</IconButton>
												</Tooltip>
											</div>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{
										displayDistrictRows.length === 0 &&
										<TableRow>
											<TableCell colSpan={shouldUseParentColumn ? 2 : 1}>
												<Typography variant='body2' color='textSecondary'>
													No districts of type {humanizeDistrictType(selectedDistrictType || 'district', stateConfig?.typeConfigurations || null)}.
												</Typography>
											</TableCell>
										</TableRow>
									}
									{displayDistrictRows.map((districtRow) => {
										const isPreviewRow = 'draftId' in districtRow;
										const rowKey = isPreviewRow ? districtRow.draftId : districtRow._id;
										const rowParent = isPreviewRow
											? getParentDisplayNameFromRawValue(districtRow.parent)
											: getDistrictParentDisplayName(districtRow as District);
										const rowName = districtRow.longName;
										const rowFips = isPreviewRow ? null : districtRow.fipsCode;
										const rowHasGeodistrict = isPreviewRow ? false : Boolean((districtRow as District).hasGeodistrict);
										const isSelected = !isPreviewRow && selectedDistrict?._id === districtRow._id;
										return (
											<TableRow
												key={rowKey}
												style={{
													backgroundColor: isSelected ? '#CDDDFA' : 'white',
													cursor: isPreviewRow ? 'default' : 'pointer',
													height: '57px'
												}}
												onClick={() => {
													if (isPreviewRow) {
														return;
													}
													if(selectedDistrict?._id === districtRow._id) {
														setSelectedDistrict(null)
													} else {
														setSelectedDistrict(districtRow as District)
													}
												}}
											>
												{
													shouldUseParentColumn &&
													<TableCell style={{ paddingTop: '4px', paddingBottom: '4px' }}>
														<Typography variant='body1' style={{ whiteSpace: 'nowrap' }}>{rowParent}</Typography>
													</TableCell>
												}
												<TableCell style={{ paddingTop: '4px', paddingBottom: '4px' }}>
													<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
														{rowHasGeodistrict && (
															<Tooltip title="District already has a geodistrict">
																<div style={{ display: 'flex', alignItems: 'center' }}>
																	<Map style={{ fontSize: '16px', color: '#4CAF50' }} />
																	<CheckCircle style={{ fontSize: '14px', color: '#4CAF50', marginLeft: '-6px', marginBottom: '-6px' }} />
																</div>
															</Tooltip>
														)}
														<div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
															<Typography variant='body1' style={{ whiteSpace: 'nowrap', lineHeight: 1.1 }}>{rowName}</Typography>
															{
																rowFips &&
																<Typography variant='caption' style={{ color: '#757575', whiteSpace: 'nowrap', lineHeight: 1.1, marginTop: '2px' }}>
																	FIPS: {rowFips}
																</Typography>
															}
														</div>
													</div>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</SplitInner>
					<DashedLineContainer numRows={displayDistrictRows.length}>
						<div/>
						{
							Array.from({ length: lineCount }).map((_, idx) => (
								<DashedLine key={idx} />
							))
						}
					</DashedLineContainer>
					<SplitInner>
						<TableContainer component={Paper} style={{ width: '100%', overflowX: 'scroll' }}>
							<Table>
								<TableHead sx={tableHeaderSx}>
									<TableRow>
										{
											onDeleteShapefileRow &&
											<TableCell padding='checkbox' />
										}
										{
											shapefileHeaders.map(header => (
												<TableCell key={header}>
													<div style={{ display: 'flex', alignItems: 'center', gap: '4px'  }}>
														<Typography variant='body2' style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{header}</Typography>
														<Tooltip title='Filter by this column'>
															<IconButton
																size='small'
																onClick={(e) => openColumnFilter(header, e.currentTarget)}
																style={(columnFilters[header] || []).length > 0
																	? { backgroundColor: '#DCE8FF' }
																	: {}
																}
															>
																<FilterList style={{ fontSize: '16px' }}/>
															</IconButton>
														</Tooltip>
														<Tooltip title='More options'>
															<IconButton
																size='small'
																onClick={(e) => openColumnActionsMenu(header, e.currentTarget)}
															>
																<MoreVert style={{ fontSize: '16px' }}/>
															</IconButton>
														</Tooltip>
														{
															populationColumnPotentials.includes(header) &&
															<Tooltip title='Set as population'>
																<IconButton size='small' onClick={() => setPopulationColumn(header)}>
																	{
																		populationColumn === header
																		? <PeopleShadedIn style={{ fontSize: '16px' }} color='primary'/>
																		: <PeopleNotShadedIn style={{ fontSize: '16px', opacity: 0.6 }}/>
																	}
																</IconButton>
															</Tooltip>
														}
													</div>
												</TableCell>
											))
										}
									</TableRow>
								</TableHead>
								<TableBody>
									{shapefileRows.map((shapefileRow) => (
										<TableRow key={shapefileRow.dataIndex} style={{
											cursor: selectedDistrict ? 'pointer' : 'default',
										}} onClick={() => {
											if(selectedDistrict) {
												pairChildDistrictToShapefileRow(selectedDistrict, shapefileRow)
											}
										}}>
											{
												onDeleteShapefileRow &&
												<TableCell padding='checkbox'>
													<Tooltip title='Delete row'>
														<span>
															<IconButton
																size='small'
																color='error'
																disabled={deletingShapefileRows.includes(shapefileRow.dataIndex)}
																onClick={(event) => {
																	event.stopPropagation();
																	deleteShapefileRow(shapefileRow.dataIndex);
																}}
															>
																{
																	deletingShapefileRows.includes(shapefileRow.dataIndex)
																		? <CircularProgress size={16} />
																		: <DeleteOutline style={{ fontSize: '18px' }} />
																}
															</IconButton>
														</span>
													</Tooltip>
												</TableCell>
											}
											{
												shapefileHeaders.map((key, idx) => (
													<TableCell key={idx}>
														<Typography variant='body1' style={{ whiteSpace: 'nowrap'}}>{shapefileRow[key]}</Typography>
													</TableCell>
												))
											}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</SplitInner>
				</TwoSideSplit>
			</div>
			<Menu
				open={Boolean(openFilterColumn && filterMenuAnchor)}
				anchorEl={filterMenuAnchor}
				onClose={closeColumnFilter}
			>
				<MenuItem
					onClick={() => {
						if (openFilterColumn) {
							clearColumnFilter(openFilterColumn);
						}
					}}
				>
					Clear filter
				</MenuItem>
				{
					(openFilterColumn ? uniqueValuesByHeader[openFilterColumn] || [] : []).map((value) => {
						const selected = Boolean(openFilterColumn && (columnFilters[openFilterColumn] || []).includes(value));
						return (
							<MenuItem
								key={value}
								onClick={() => {
									if (openFilterColumn) {
										toggleColumnFilterValue(openFilterColumn, value);
									}
								}}
							>
								<Checkbox checked={selected} size="small" />
								<ListItemText primary={value || '(blank)'} />
							</MenuItem>
						);
					})
				}
			</Menu>
			<Menu
				open={Boolean(openDistrictFilterColumn && districtFilterMenuAnchor)}
				anchorEl={districtFilterMenuAnchor}
				onClose={closeDistrictColumnFilter}
			>
				<MenuItem
					onClick={() => {
						if (openDistrictFilterColumn) {
							clearDistrictColumnFilter(openDistrictFilterColumn);
						}
					}}
				>
					Clear filter
				</MenuItem>
				{
					(openDistrictFilterColumn ? uniqueDistrictValuesByColumn[openDistrictFilterColumn] || [] : []).map((value) => {
						const selected = Boolean(openDistrictFilterColumn && (districtColumnFilters[openDistrictFilterColumn] || []).includes(value));
						return (
							<MenuItem
								key={value}
								onClick={() => {
									if (openDistrictFilterColumn) {
										toggleDistrictColumnFilterValue(openDistrictFilterColumn, value);
									}
								}}
							>
								<Checkbox checked={selected} size="small" />
								<ListItemText primary={value || '(blank)'} />
							</MenuItem>
						);
					})
				}
			</Menu>
			<Menu
				open={Boolean(openColumnActions && columnActionsAnchor)}
				anchorEl={columnActionsAnchor}
				onClose={closeColumnActionsMenu}
			>
				<MenuItem
					onClick={() => {
						if (openColumnActions) {
							openTransformColumnModal(openColumnActions);
						}
						closeColumnActionsMenu();
					}}
				>
					Transform column
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (openColumnActions) {
							hideShapefileColumn(openColumnActions);
						}
						closeColumnActionsMenu();
					}}
				>
					Hide column
				</MenuItem>
				{
					hiddenShapefileColumns.length > 0 &&
					<>
						<Divider />
						<MenuItem
							onClick={() => {
								setHiddenShapefileColumns([]);
								closeColumnActionsMenu();
							}}
						>
							Show hidden columns
						</MenuItem>
					</>
				}
				<Divider />
				{
					shouldUseParentColumn
						? (
							<>
								<MenuItem
									onClick={() => {
										if (openColumnActions) {
											setParentMatchColumn(openColumnActions);
										}
										closeColumnActionsMenu();
									}}
								>
									Match by this parent column{openColumnActions && parentMatchColumn === openColumnActions ? ' (selected)' : ''}
								</MenuItem>
								<MenuItem
									disabled={noDistrictsForSelectedType && !parentMatchColumn}
									onClick={() => {
										if (openColumnActions && parentMatchColumn) {
											if (noDistrictsForSelectedType) {
												setPrimaryMatchColumnIfValid(openColumnActions);
											} else {
												attemptMatchByParentAndColumn(parentMatchColumn, openColumnActions);
											}
										}
										closeColumnActionsMenu();
									}}
								>
									{
										noDistrictsForSelectedType
											? `Make this primary column${openColumnActions && primaryMatchColumn === openColumnActions ? ' (selected)' : ''}`
											: 'Match by this column'
									}
								</MenuItem>
								{
									!noDistrictsForSelectedType &&
									<MenuItem
										disabled={!parentMatchColumn}
										onClick={() => {
											if (openColumnActions && parentMatchColumn) {
												attemptMatchByParentAndFipsCode(parentMatchColumn, openColumnActions);
											}
											closeColumnActionsMenu();
										}}
									>
										Match by FIPS code
									</MenuItem>
								}
							</>
						)
						: (
							<>
								<MenuItem
									onClick={() => {
										if (openColumnActions) {
											if (noDistrictsForSelectedType) {
												setPrimaryMatchColumnIfValid(openColumnActions);
											} else {
												attemptMatchByColumn(openColumnActions);
											}
										}
										closeColumnActionsMenu();
									}}
								>
									{
										noDistrictsForSelectedType
											? `Make this primary column${openColumnActions && primaryMatchColumn === openColumnActions ? ' (selected)' : ''}`
											: 'Match by this column'
									}
								</MenuItem>
								{
									!noDistrictsForSelectedType &&
									<MenuItem
										onClick={() => {
											if (openColumnActions) {
												attemptMatchByFipsCode(openColumnActions);
											}
											closeColumnActionsMenu();
										}}
									>
										Match by FIPS code
									</MenuItem>
								}
							</>
						)
				}
			</Menu>
			<TransformColumnModal
				open={transformColumnModalOpen}
				sourceColumn={transformColumnModalSourceColumn}
				existingHeaders={allShapefileHeaders}
				sampleRows={transformColumnModalRows}
				sampleIndexes={transformColumnModalSampleIndexes}
				onClose={closeTransformColumnModal}
				onApply={applyTransformColumnDefinition}
			/>
			<div style={{ margin: '24px 0', display: 'flex', gap: '60px', alignItems: 'center' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{
						noDistrictsForSelectedType && primaryMatchColumn
						? (previewRows.length > 0 ? <CheckCircle style={{ color: 'green' }} /> : <PauseCircleOutline style={{ }} />)
						: numberPaired === districtRows.length
						? <CheckCircle style={{ color: 'green' }} />
						: <PauseCircleOutline style={{ }} />
					}
					<Typography variant='body1'>
						{
							noDistrictsForSelectedType && primaryMatchColumn
								? `Will create ${previewRows.length} districts`
								: `${numberPaired} of ${filteredActiveDistrictRows?.length} paired`
						}
					</Typography>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{
						populationColumn
						? <CheckCircle style={{ color: 'green' }} />
						: <PauseCircleOutline style={{ }} />
					}
					<Typography variant='body1'>{populationColumn ? 'Population column identified' : 'No population' }</Typography>
				</div>
			</div>
		</div>
	)
}

function valueForMatch(value: string) {
	return value.toLowerCase()
				.replaceAll(/district|dist|hd|sd|cng|dst|parish/g, '')
				.replaceAll(/-/g, '')
				.trim()
}

const TwoSideSplit = styled.div`
	display: grid;
	width: 100%;
	max-width:100%;
	grid-template-columns: 300px 24px calc(100% - 24px - 300px);
`

interface DashedLineContainerProps {
	numRows: number;
}

const DashedLineContainer = styled.div<DashedLineContainerProps>`
	display: grid;
	grid-template-columns: 24px;
	grid-template-rows: repeat(${props => props.numRows}, 57px);
	align-items: center;
`
const DashedLine = styled.div`
	border-top: dashed 2px #AAAAAA;
	height: 1px;
	width: 100%;
`

const SplitInner = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

export { ShapefileDataMatcher }